import { React, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import UseProfileData from '../plugin/UseProfileData';
import apiInstance from '../../utils/axios';
import UserData from '../plugin/UserData';
import { 
    User, 
    ShoppingBag, 
    Heart, 
    Bell, 
    Settings, 
    LogOut, 
    Edit3,
    RefreshCw,
    Package,
    ChevronRight
} from 'lucide-react';

function Sidebar() {
    const userProfile = UseProfileData();
    const userData = UserData();
    const location = useLocation();
    let [loading, setLoading] = useState(true);
    const [notificationCount, setNotificationCount] = useState(0);
    const [orderCount, setOrderCount] = useState(0);
    const [wishlistCount, setWishlistCount] = useState(0);

    useEffect(() => {
        if (userProfile) {
            setLoading(false);
        }
    });

    // Fetch real-time counts
    useEffect(() => {
        if (userData?.user_id) {
            apiInstance.get(`customer/notification/${userData.user_id}/`)
                .then((res) => setNotificationCount(res.data.length))
                .catch((error) => console.error('Error fetching notifications:', error));

            apiInstance.get(`customer/orders/${userData.user_id}/`)
                .then((res) => setOrderCount(res.data.length))
                .catch((error) => console.error('Error fetching orders:', error));

            apiInstance.get(`customer/wishlist/${userData.user_id}/`)
                .then((res) => setWishlistCount(res.data.length))
                .catch((error) => console.error('Error fetching wishlist:', error));
        }
    }, [userData?.user_id]);

    const isActive = (path) => location.pathname === path || location.pathname === path + '/';

    const menuItems = [
        { path: '/customer/account/', icon: User, label: 'Mi Cuenta', color: '#3b82f6', bgColor: '#eff6ff' },
        { path: '/customer/orders/', icon: ShoppingBag, label: 'Mis Pedidos', color: '#10b981', bgColor: '#ecfdf5', count: orderCount },
        { path: '/customer/wishlist/', icon: Heart, label: 'Lista de Deseos', color: '#ef4444', bgColor: '#fef2f2', count: wishlistCount },
        { path: '/customer/notifications/', icon: Bell, label: 'Notificaciones', color: '#f59e0b', bgColor: '#fffbeb', count: notificationCount, countColor: '#ef4444' },
        { path: '/customer/settings/', icon: Settings, label: 'Configuración', color: '#8b5cf6', bgColor: '#f5f3ff' },
    ];

    return (
        <div className="sidebar-wrapper">
            {!loading && (
                <>
                    {/* Profile Card */}
                    <div className="profile-card">
                        <div className="profile-bg-pattern" />
                        <div className="profile-content">
                            <div className="profile-image-wrapper">
                                <img
                                    src={userProfile?.image || '/placeholder.svg'}
                                    alt="Perfil"
                                />
                                <div className="online-indicator" />
                            </div>
                            <h3 className="profile-name">{userProfile?.full_name}</h3>
                            <p className="profile-email">{userProfile?.user?.email}</p>
                            <div className="profile-actions">
                                <Link to="/customer/settings/" className="edit-btn">
                                    <Edit3 size={16} />
                                    Editar Perfil
                                </Link>
                                <button 
                                    className="refresh-btn"
                                    onClick={() => window.location.reload()}
                                    title="Actualizar"
                                >
                                    <RefreshCw size={16} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Navigation Menu */}
                    <nav className="sidebar-nav">
                        {menuItems.map((item) => {
                            const Icon = item.icon;
                            const active = isActive(item.path);
                            return (
                                <Link 
                                    key={item.path} 
                                    to={item.path} 
                                    className={`nav-item ${active ? 'active' : ''}`}
                                >
                                    <div 
                                        className="nav-icon" 
                                        style={{ 
                                            backgroundColor: active ? item.color : item.bgColor,
                                            color: active ? 'white' : item.color 
                                        }}
                                    >
                                        <Icon size={20} />
                                    </div>
                                    <span className="nav-label">{item.label}</span>
                                    {item.count > 0 && (
                                        <span 
                                            className="nav-badge"
                                            style={{ backgroundColor: item.countColor || '#3b82f6' }}
                                        >
                                            {item.count}
                                        </span>
                                    )}
                                    <ChevronRight size={18} className="nav-arrow" />
                                </Link>
                            );
                        })}

                        <div className="nav-divider" />

                        <Link to="/logout" className="nav-item logout">
                            <div className="nav-icon" style={{ backgroundColor: '#fef2f2', color: '#ef4444' }}>
                                <LogOut size={20} />
                            </div>
                            <span className="nav-label">Cerrar Sesión</span>
                            <ChevronRight size={18} className="nav-arrow" />
                        </Link>
                    </nav>

                    {/* Quick Stats (Mobile) */}
                    <div className="mobile-stats">
                        <div className="stat-item">
                            <Package size={20} color="#3b82f6" />
                            <span className="stat-count">{orderCount}</span>
                            <span className="stat-label">Pedidos</span>
                        </div>
                        <div className="stat-item">
                            <Heart size={20} color="#ef4444" />
                            <span className="stat-count">{wishlistCount}</span>
                            <span className="stat-label">Favoritos</span>
                        </div>
                        <div className="stat-item">
                            <Bell size={20} color="#f59e0b" />
                            <span className="stat-count">{notificationCount}</span>
                            <span className="stat-label">Alertas</span>
                        </div>
                    </div>
                </>
            )}

            <style>{`
                .sidebar-wrapper {
                    width: 100%;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                }

                /* Profile Card */
                .profile-card {
                    position: relative;
                    background: white;
                    border-radius: 24px;
                    overflow: hidden;
                    margin-bottom: 20px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
                }

                .profile-bg-pattern {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 80px;
                    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
                }

                .profile-bg-pattern::after {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background: 
                        radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%),
                        radial-gradient(circle at 80% 50%, rgba(168, 85, 247, 0.2) 0%, transparent 50%);
                }

                .profile-content {
                    position: relative;
                    padding: 50px 24px 24px;
                    text-align: center;
                }

                .profile-image-wrapper {
                    position: relative;
                    width: 90px;
                    height: 90px;
                    margin: 0 auto 16px;
                }

                .profile-image-wrapper img {
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;
                    object-fit: cover;
                    border: 4px solid white;
                    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
                }

                .online-indicator {
                    position: absolute;
                    bottom: 4px;
                    right: 4px;
                    width: 18px;
                    height: 18px;
                    background: #10b981;
                    border: 3px solid white;
                    border-radius: 50%;
                }

                .profile-name {
                    font-size: 18px;
                    font-weight: 700;
                    color: #0f172a;
                    margin: 0 0 4px;
                }

                .profile-email {
                    font-size: 13px;
                    color: #64748b;
                    margin: 0 0 20px;
                }

                .profile-actions {
                    display: flex;
                    gap: 8px;
                    justify-content: center;
                }

                .edit-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 20px;
                    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
                    color: white;
                    border: none;
                    border-radius: 12px;
                    font-size: 14px;
                    font-weight: 600;
                    text-decoration: none;
                    transition: all 0.2s ease;
                }

                .edit-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.2);
                    color: white;
                }

                .refresh-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 42px;
                    height: 42px;
                    background: #f1f5f9;
                    border: none;
                    border-radius: 12px;
                    color: #64748b;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .refresh-btn:hover {
                    background: #e2e8f0;
                    color: #0f172a;
                }

                /* Navigation */
                .sidebar-nav {
                    background: white;
                    border-radius: 24px;
                    padding: 12px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
                }

                .nav-item {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    padding: 14px 16px;
                    border-radius: 14px;
                    text-decoration: none;
                    color: #374151;
                    transition: all 0.2s ease;
                    margin-bottom: 4px;
                }

                .nav-item:hover {
                    background: #f8fafc;
                }

                .nav-item.active {
                    background: #f1f5f9;
                }

                .nav-item.active .nav-label {
                    color: #0f172a;
                    font-weight: 600;
                }

                .nav-icon {
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    flex-shrink: 0;
                }

                .nav-label {
                    flex: 1;
                    font-size: 15px;
                    font-weight: 500;
                }

                .nav-badge {
                    padding: 4px 10px;
                    border-radius: 100px;
                    font-size: 12px;
                    font-weight: 700;
                    color: white;
                }

                .nav-arrow {
                    color: #cbd5e1;
                    transition: transform 0.2s ease;
                }

                .nav-item:hover .nav-arrow {
                    transform: translateX(4px);
                    color: #94a3b8;
                }

                .nav-divider {
                    height: 1px;
                    background: #e2e8f0;
                    margin: 12px 8px;
                }

                .nav-item.logout .nav-label {
                    color: #ef4444;
                }

                .nav-item.logout:hover {
                    background: #fef2f2;
                }

                /* Mobile Stats */
                .mobile-stats {
                    display: none;
                    background: white;
                    border-radius: 20px;
                    padding: 20px;
                    margin-top: 20px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
                }

                .stat-item {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 4px;
                }

                .stat-count {
                    font-size: 20px;
                    font-weight: 700;
                    color: #0f172a;
                }

                .stat-label {
                    font-size: 12px;
                    color: #64748b;
                }

                @media (max-width: 991px) {
                    .sidebar-wrapper {
                        margin-bottom: 24px;
                    }

                    .mobile-stats {
                        display: flex;
                        justify-content: space-around;
                    }

                    .sidebar-nav {
                        display: none;
                    }
                }

                @media (max-width: 480px) {
                    .profile-card {
                        border-radius: 20px;
                    }

                    .profile-content {
                        padding: 40px 16px 20px;
                    }

                    .profile-image-wrapper {
                        width: 80px;
                        height: 80px;
                    }

                    .profile-name {
                        font-size: 16px;
                    }

                    .edit-btn {
                        padding: 8px 16px;
                        font-size: 13px;
                    }
                }
            `}</style>
        </div>
    );
}

export default Sidebar;